import styles from "./DadosBancarios.module.scss";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Input } from "shared/components/inputs/Input";
import { BancoOption, buscarBancosOptions } from "shared/services/api/bancos.api";
import { maskAgencia, maskConta } from "shared/utils/masks";

const tipoContaOptions = [
  { value: "poupanca", label: "Conta Poupança" },
  { value: "corrente", label: "Conta Corrente" },
];

export interface DadosBancariosFormData {
  banco: string;
  agencia: string;
  conta: string;
  tipoConta: string;
}

interface DadosBancariosProps {
  onNext?: (data: DadosBancariosFormData) => void;
  defaultValues?: Partial<DadosBancariosFormData>;
  modoEdicao?: boolean;
  onSalvo?: (dados: DadosBancariosFormData) => void;
}

export const DadosBancarios: React.FC<DadosBancariosProps> = ({
  onNext,
  defaultValues,
  modoEdicao = false,
  onSalvo,
}) => {
  const [bancosOptions, setBancosOptions] = useState<BancoOption[]>([]);
  const [carregandoBancos, setCarregandoBancos] = useState(true);
  const [erroBancos, setErroBancos] = useState("");

  const { register, handleSubmit, watch, setValue, formState: { errors } } =
    useForm<DadosBancariosFormData>({
      defaultValues: {
        banco: defaultValues?.banco ?? "",
        agencia: defaultValues?.agencia ?? "",
        conta: defaultValues?.conta ?? "",
        tipoConta: defaultValues?.tipoConta ?? "",
      },
    });

  const bancoSelecionado = watch("banco");
  const tipoContaSelecionado = watch("tipoConta");

  useEffect(() => {
    let ativo = true;

    const carregarBancos = async () => {
      try {
        setCarregandoBancos(true);
        setErroBancos("");
        const bancos = await buscarBancosOptions();
        if (ativo) setBancosOptions(bancos);
      } catch {
        if (ativo) setErroBancos("Não foi possível carregar a lista de bancos.");
      } finally {
        if (ativo) setCarregandoBancos(false);
      }
    };

    carregarBancos();
    return () => {
      ativo = false;
    };
  }, []);

  const onSubmit = (data: DadosBancariosFormData) => {
    if (modoEdicao) {
      onSalvo?.(data);
      return;
    }
    onNext?.(data);
  };

  return (
    <form id="dados-bancarios-loja-form" onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <div className={styles.containerLabel}>
        <label className={`${styles.label} ${styles.required}`}>Banco</label>
        <Input
          type="select-with-logo"
          name="banco"
          options={bancosOptions}
          value={bancoSelecionado}
          onChange={(e: any) => setValue("banco", e.target.value, { shouldValidate: true, shouldDirty: true })}
          placeholder={carregandoBancos ? "Carregando bancos..." : "Procure seu banco"}
          register={register("banco", { required: "Banco obrigatório" })}
          error={errors.banco?.message || erroBancos}
          customClass={styles.selectBanco}
        />
      </div>

      <div className={styles.containerLabel}>
        <label className={`${styles.label} ${styles.required}`}>Agência</label>
        <Input
          type="text"
          name="agencia"
          placeholder="Digite a agência"
          register={register("agencia", {
            required: "Agência obrigatória",
            pattern: {
              value: /^\d{4}(-\d)?$/,
              message: "Informe a agência com 4 números e, se houver, o dígito",
            },
            onChange: (e) => {
              const valor = maskAgencia(e.target.value);
              e.target.value = valor;
              setValue("agencia", valor, { shouldValidate: true, shouldDirty: true });
            },
          })}
          error={errors.agencia?.message}
          inputMode="numeric"
          maxLength={6}
          customClass={styles.customInput}
        />
      </div>

      <div className={styles.containerLabel}>
        <label className={`${styles.label} ${styles.required}`}>Conta</label>
        <Input
          type="text"
          name="conta"
          placeholder="Digite o número da conta"
          register={register("conta", {
            required: "Conta obrigatória",
            pattern: {
              value: /^\d{1,20}-[0-9X]$/i,
              message: "Informe o número da conta com o dígito",
            },
            onChange: (e) => {
              const valor = maskConta(e.target.value);
              e.target.value = valor;
              setValue("conta", valor, { shouldValidate: true, shouldDirty: true });
            },
          })}
          error={errors.conta?.message}
          inputMode="numeric"
          maxLength={22}
          customClass={styles.customInput}
        />
      </div>

      <div className={styles.containerLabel}>
        <label className={`${styles.label} ${styles.required}`}>Tipo de Conta</label>
        <Input
          type="select"
          name="tipoConta"
          options={tipoContaOptions}
          value={tipoContaSelecionado}
          onChange={(e: any) => setValue("tipoConta", e.target.value, { shouldValidate: true, shouldDirty: true })}
          placeholder="Selecione o tipo de conta"
          register={register("tipoConta", { required: "Tipo de conta obrigatório" })}
          error={errors.tipoConta?.message}
          customClass={styles.customInputSelect}
        />
      </div>
    </form>
  );
};